import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, catchError, map, of, switchMap, take } from 'rxjs';
import { ApplicationContextService } from 'src/app/services/application-context.service';
import { DisclosureProcessingService } from 'src/app/services/disclosure-processing.service';

const DISCLOSURES_PATH = 'disclosures';

// Screens the user is still allowed to reach while a package is generating. Submission stays open because
// TPO users need to be able to submit the loan while disclosures finish.
const ALLOWED_PATHS: readonly string[] = [DISCLOSURES_PATH, 'submission'];

/**
 * Keeps the user on the Disclosures screen while a disclosure package is still being generated, so the
 * loan cannot be navigated through or edited mid-generation. TPO side and PRMG only - they are the only
 * companies running the streamlined disclosure wizard, which queues generation on the backend and lets
 * the user leave the wizard while it finishes.
 */
@Injectable({ providedIn: 'root' })
export class CanActivateDuringDisclosureProcessingGuard {
  constructor(
    private readonly _applicationContextService: ApplicationContextService,
    private readonly _disclosureProcessingService: DisclosureProcessingService,
    private readonly _router: Router
  ) { }

  canActivateChild(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    // TPO only - the lock exists for the broker-facing wizard flow; internal admin users keep full
    // navigation while a package generates.
    if (!state.url.startsWith('/tpo')) {
      return of(true);
    }

    const applicationId = this.getApplicationId(next);
    if (applicationId === null || this.isAllowedUrl(state.url)) {
      return of(true);
    }
    if (Number.isNaN(applicationId)) {
      // A loan-scoped route with an unusable id can't be checked against the backend, and letting it
      // through would be a way around the lock, so refuse the navigation instead.
      return of(false);
    }

    return this._applicationContextService.context.pipe(
      take(1),
      switchMap(context => {
        if (!context?.isCompanyPRMG) {
          return of(true);
        }
        return this._disclosureProcessingService.resolve(applicationId).pipe(
          switchMap(isProcessing => {
            if (!isProcessing) {
              return this._applicationContextService.refreshLoanReadOnlyStatus(applicationId).pipe(
                map(() => true),
                catchError(() => of(false))
              );
            }
            return of(this._router.createUrlTree(
              [`/tpo/app-details/${applicationId}/${DISCLOSURES_PATH}`]
            ));
          })
        );
      })
    );
  }

  private isAllowedUrl = (url: string): boolean => {
    const segments = url.split('?')[0].split('/');
    return ALLOWED_PATHS.some(path => segments.includes(path));
  };

  /**
   * Returns the id of the loan the route belongs to, or null when the route carries no `id` at all.
   * An `id` that is present but not a valid loan id (0, negative, non-numeric) resolves to NaN so the
   * guard treats the route as un-resolvable and keeps the user on the Disclosures screen rather than
   * silently letting the navigation through.
   */
  private getApplicationId = (route: ActivatedRouteSnapshot): number | null => {
    let current: ActivatedRouteSnapshot | null = route;
    while (current) {
      const rawId = current.params['id'];
      if (rawId !== undefined && rawId !== null && rawId !== '') {
        const id = Number(rawId);
        return Number.isInteger(id) && id > 0 ? id : Number.NaN;
      }
      current = current.parent;
    }
    return null;
  };
}
